import { baseAppApi } from '@/core/network/baseAppApi';
import { COMMON_HEADERS } from '@/core/network/network.constants';
import { getFirstErrorOrDefault } from '@/core/network/network.utils';
import { getBoardId } from '@/core/web-storage/webStorage.utils';
import { transformValidateServiceResponse } from '@/validation-service/services/validationApi.utils';
import tokens from '@/validation-service/tokens.json';

export type ValidationServiceErrorInner = {
  id: number;
  description: string;
  severity: 'Warning' | 'Error' | 'Critical';
};

export type ValidationServiceErrorResponse = {
  sin: string;
  startSchoolYear: string;
  validationErrors: {
    sourceId: number;
    validationErrors: ValidationServiceErrorInner[];
  }[];
};

export type ValidateBatchMinifiedResponse = {
  errors: number;
  warnings: number;
};
export type ValidateBatchResponse = {
  batchValidationErrorCount: number;
  batchValidationWarningCount: number;
};

export type ValidateUnsubmittedResponse = {
  sourceId: number;
  validationErrors: ValidationServiceErrorInner[];
}[];

export type ValidateUnsubmittedMinifiedResponse = {
  errors: number;
  warnings: number;
};

type InnerCalculation = {
  order: number;
  name: string;
  formulaText: string;
  formulaValues: string;
  total: number;
  finalValue: boolean;
};
type GetCalculationsResponse = Record<
  string,
  {
    sin: string;
    startSchoolYear: string;
    contributionCalculationData: {
      sourceId: number;
      calculationType: string;
      calculationData: InnerCalculation[];
    }[];
    ValidationErrors: {
      validationErrors: {
        id: number;
        description: string;
      }[];
    }[];
  }
>;
export type MinifiedGetCalculationsResponse = {
  calculations: {
    title: keyof CalculationMainTitleLabels;
    calculations: InnerCalculation[];
  }[];
  errors: {
    id: number;
    description: string;
  }[];
  totals: Record<keyof CalculationMainTitleLabels, number | undefined>;
};
export type CalculationMainTitleLabels = {
  CalculatePensionableSalaryPaid: 'PSP';
  CalculateContributionsPayable: 'Contributions';
  CalculateAtaFees: 'ATA Fees';
  CalculatePayout: 'Payout';
};

const validationApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    validateBatch: build.mutation<ValidateBatchMinifiedResponse, { employerId: number }>({
      query({ employerId }) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/contribution-calculator/employers/${boardId}/validatebatch`,
          body: {
            employerId,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: ValidateBatchResponse) {
        return {
          errors: response.batchValidationErrorCount,
          warnings: response.batchValidationWarningCount,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['VALIDATE_ERROR']);
      },
      invalidatesTags: ['ERTN_EMPLOYEES'],
    }),
    validateUnsubmitted: build.mutation<
      ValidateUnsubmittedMinifiedResponse,
      { employeeIds: number[] }
    >({
      query({ employeeIds }) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/contribution-calculator/employers/${boardId}/validateemployee`,
          body: {
            employeeIds,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: ValidateUnsubmittedResponse) {
        return transformValidateServiceResponse(response);
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['VALIDATE_EMPLOYEE_ERROR']);
      },
    }),
    getCalculations: build.mutation<MinifiedGetCalculationsResponse, { contributionId: number }>({
      query({ contributionId }) {
        const boardId = getBoardId();
        return {
          method: 'POST',
          url: `/v1/contribution-calculator/employers/${boardId}/calculate`,
          body: {
            contributionIds: [contributionId],
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: GetCalculationsResponse) {
        let minified = [] as MinifiedGetCalculationsResponse['calculations'];
        const totals = {} as MinifiedGetCalculationsResponse['totals'];
        let errors = [] as MinifiedGetCalculationsResponse['errors'];

        const calculationsData = Object.values(response)[0]?.contributionCalculationData;
        const errorsData = Object.values(response)[0].ValidationErrors[0];

        if (errorsData && errorsData?.validationErrors !== undefined) {
          errors = errorsData.validationErrors.map(({ id, description }) => ({
            id,
            description,
          }));
        }

        if (calculationsData) {
          minified = calculationsData
            .filter((item) => item.calculationData.length > 0)
            .map((item) => {
              return {
                title: item.calculationType as keyof CalculationMainTitleLabels,
                calculations: item.calculationData,
              };
            });

          const sortOrder: Record<keyof CalculationMainTitleLabels, number> = {
            CalculatePensionableSalaryPaid: 0,
            CalculateContributionsPayable: 1,
            CalculateAtaFees: 2,
            CalculatePayout: 3,
          };

          minified.sort((left, right) => {
            const leftOrder = sortOrder[left.title];
            const rightOrder = sortOrder[right.title];

            if (leftOrder !== undefined && rightOrder !== undefined) {
              return leftOrder - rightOrder;
            }

            return 0;
          });

          minified.forEach((item) => {
            const total = item.calculations
              .filter((calculation) => calculation.finalValue === true)
              .map((calculation) => calculation.total);
            totals[item.title] = total.length > 0 ? total[0] : undefined;
          });
        }

        return { calculations: minified, totals, errors };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['CALCULATIONS_ERROR']);
      },
    }),
  }),
});

export default validationApi;
